import { EProjectType } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IWithSDProps } from "../../../../shared/hoc/screen-design-consumer";
import { PropertyHelper } from "../../../../shared/utility/property-helper";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";

export interface IPropertyFormSelectPropertyState {
  value: string | string[];
}

export interface IPropertyFormSelectPropertyOption {
  value: string | number;
  label: string;
  isDefault?: boolean;
}

export interface IPropertyFormSelectPropertyProps {
  value: string;
  options: IPropertyFormSelectPropertyOption[];
  /** A row above the options — "ADD NEW", and what it opens. The list draws the row itself. */
  dropdownHeader?: React.ReactNode;
  onDropdownHeaderClick?: () => void;
  /** Draws an option's label. The value and the key stay the list's to decide. */
  optionRenderer?: (option: IPropertyFormSelectPropertyOption, index: number) => React.ReactNode;
  onChange: (value: string) => void;
  onCustomValueChange?: (value: string, propName: string) => void;
  customValue?: string;
  mode?: "multiple" | "tags";
  sd: any;
  isLoading?: boolean;
  selectClassName?: string;
}

export class PropertyFormSelectProperty extends React.PureComponent<
  IPropertyFormSelectPropertyProps & IWithSDProps,
  IPropertyFormSelectPropertyState
> {
  propertyHelper: PropertyHelper;

  constructor(props: IPropertyFormSelectPropertyProps & IWithSDProps) {
    super(props);
    this.propertyHelper = new PropertyHelper();
    let value: string | string[] = props.value;
    if (this.isCommaJoinedMode(props.mode)) {
      if (!value) value = [];
      else value = value.split(",");
    }
    this.state = {
      value
    };
  }

  componentDidUpdate = (prevProps: IPropertyFormSelectPropertyProps) => {
    if (prevProps === this.props) return;
    this.setState({ value: this.getPropertyValueFromProps() });
  };

  isCommaJoinedMode = (mode: "multiple" | "tags" | undefined): boolean => {
    return mode === "multiple" || mode === "tags";
  };

  getPropertyValueFromProps = (): any => {
    if (this.isCommaJoinedMode(this.props.mode)) {
      if (!this.props.value) return [];
      return this.props.value.split(",");
    }

    return this.props.value && this.props.value !== "" && this.props.value !== "-1" ? this.props.value : undefined;
  };

  handleChangeValue = (e: any) => {
    const value = e;
    this.setState({ value }, () => {
      if (this.props.onChange) {
        this.props.onChange(value?.toString());
      }
    });
  };

  handleChangeInputValue = (e: any) => {
    const value = e?.target?.value;
    this.setState({ value }, () => {
      if (this.props.onCustomValueChange) {
        this.props.onCustomValueChange(value?.toString(), "customFormatter");
      }
    });
  };

  /**
   * The options the list is given. A caller that wants a richer row supplies its label; the
   * value and the key are not the caller's to set, which is why the whole option element is no
   * longer handed over.
   */
  getSelectboxOptions = (): KuikaSelectboxOption[] => {
    const isMobileOnly: boolean =
      this.props.sd.project?.projectType === EProjectType.Mobile && !this.props.sd.getIsWebScreen();
    return this.props.options
      .filter((option: IPropertyFormSelectPropertyOption): boolean => {
        return !(option.value === "custom date" && isMobileOnly);
      })
      .map((option: IPropertyFormSelectPropertyOption, index: number): KuikaSelectboxOption => {
        return {
          key: `o_${index}`,
          value: option.value,
          label: this.props.optionRenderer ? this.props.optionRenderer(option, index) : option.label,
          searchLabel: option.label
        };
      });
  };

  render() {
    const selectClasses = `kuika_property-grid-column kuika_select_property kuika-right-panel__col${
      this.props.selectClassName ? ` ${this.props.selectClassName}` : ""
    }`;
    const isTagsMode: boolean = this.props.mode === "tags";
    // File-extension style tokens (".pdf") must keep their casing in tags mode
    const optionTextTransform: "capitalize" | "none" = isTagsMode ? "none" : "capitalize";
    // The rows are portalled out of the panel, so the casing the control inherits never reaches
    // them. A caller's own row keeps whatever casing it draws.
    const isListCapitalized: boolean = optionTextTransform === "capitalize" && !this.props.optionRenderer;
    return (
      <>
        <div className={selectClasses}>
          <KuikaSelectbox
            mode={this.props.mode}
            placeholder=""
            allowClear
            value={this.state.value ?? null}
            disabled={this.props.isLoading}
            style={{ width: "100%", textTransform: optionTextTransform }}
            searchable={isTagsMode}
            tokenSeparators={isTagsMode ? [","] : undefined}
            onChange={this.handleChangeValue}
            dropdownHeader={this.props.dropdownHeader}
            onDropdownHeaderClick={this.props.onDropdownHeaderClick}
            overlayClassName={isListCapitalized ? "kuika-right-panel__select-dropdown--capitalized" : undefined}
            options={this.getSelectboxOptions()}
          />
          {this.props.value === "custom date" && (
            <KuikaInput
              defaultValue={this.props.customValue}
              onBlur={this.handleChangeInputValue}
              style={{ width: "100%", marginTop: 4 }}
              placeholder="MMMM Do YYYY, h:mm:ss a"
            />
          )}
        </div>
      </>
    );
  }
}
